import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Button, Table } from "react-bootstrap";
import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";
import { ShimmerTableCol } from "@/assets/shimmer";
import downSvg from "@/assets/images/down-arrow.svg";
import upSvg from "@/assets/images/up-arrow.svg";
import { CContainer } from "@coreui/react";

const initialState = {
  queryPageIndex: 0,
};

const PAGE_CHANGED = "PAGE_CHANGED";

const reducer = (state, { type, payload }) => {
  switch (type) {
    case PAGE_CHANGED:
      return {
        ...state,
        queryPageIndex: payload,
      };
    default:
      throw new Error(`Unhandled action type: ${type}`);
  }
};

function TNTable({
  columns,
  data,
  paginationData,
  t,
  onSelectPage,
  idName = "",
  pageIndexGet = 0,
  isLoading = false,
  responsive = true,
  className = "",
  sortField = "",
  sortOrder = "",
}) {
  initialState.queryPageIndex = pageIndexGet;

  const [{ queryPageIndex }, dispatch] = React.useReducer(
    reducer,
    initialState,
  );

  const tableInstance = useReactTable({
    columns,
    data,
    initialState: {
      pagination: { pageIndex: queryPageIndex },
    },
    autoResetFilters: false,
    manualPagination: true,
    pageCount: paginationData
      ? Math.ceil(paginationData.total / paginationData.per_page)
      : 1,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    setPageIndex,
    nextPage,
    previousPage,
    getPageCount,
    getState,
  } = tableInstance;

  const pageIndex = getState().pagination?.pageIndex || 0;
  const rows = getRowModel().rows;
  const firstPageRows = rows ? rows.slice(0, paginationData?.count || 0) : [];

  React.useEffect(() => {
    if (pageIndex !== undefined) {
      onSelectPage(pageIndex);
      dispatch({ type: PAGE_CHANGED, payload: pageIndex });
    }
  }, [pageIndex, onSelectPage]);

  return (
    <>
      <Table
        responsive={responsive}
        className={`text-start table-sortable booking-list mt-3${className ? ` ${className}` : ""}`}
        id={idName}
      >
        {/* 🔹 Table Header (Fixed getHeaderProps Error) */}
        <thead className="align-middle">
          {getHeaderGroups().map((headerGroup) => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(({ column }) => (
                  <th key={column.id}>
                    <span
                      className="d-flex gap-2 align-items-center justify-content-between"
                      type="button"
                      onClick={() => {
                        if (column.columnDef.enableSorting) {
                          column.toggleSorting();
                        }
                      }}
                    >
                      {flexRender(column.columnDef.header, column)}{" "}
                      {column.columnDef.enableSorting ? (
                        <div className="d-flex flex-column">
                          <img
                            src={upSvg}
                            alt="up-svg"
                            width={9}
                            height={11}
                            className={`${
                              (
                                sortField
                                  ? sortField === column.columnDef.sortField &&
                                    sortOrder === "asc"
                                  : column.getIsSorted() === "asc"
                              )
                                ? "opacity-100"
                                : "opacity-25"
                            }`}
                          />
                          <img
                            src={downSvg}
                            alt="down-svg"
                            width={9}
                            height={11}
                            className={`${
                              (
                                sortField
                                  ? sortField === column.columnDef.sortField &&
                                    sortOrder === "desc"
                                  : column.getIsSorted() === "desc"
                              )
                                ? "opacity-100"
                                : "opacity-25"
                            }`}
                          />
                        </div>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>

        {/* 🔹 Table Body (Fixed getCellProps and renderCell Errors) */}
        <tbody>
          {isLoading ? (
            Array.from({ length: 10 }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((_, colIndex) => (
                  <td key={colIndex}>
                    <ShimmerTableCol col={1} />
                  </td>
                ))}
              </tr>
            ))
          ) : firstPageRows.length === 0 ? (
            <tr>
              <td className="text-center" colSpan={columns.length}>
                No data found
              </td>
            </tr>
          ) : (
            firstPageRows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="align-content-center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <br />
      {/* 🔹 Pagination Controls */}
      {Number(paginationData?.total) > Number(paginationData?.per_page) && (
        <CContainer fluid className="paginationtable">
          <Row className={firstPageRows.length === 0 ? "d-none" : ""}>
            <Col lg={12} xs={12}>
              <div className="tablepagination">
                <Button
                  onClick={() => setPageIndex(0)}
                  disabled={!getCanPreviousPage()}
                >
                  {"<<"}
                </Button>
                <Button
                  onClick={() => previousPage()}
                  disabled={!getCanPreviousPage()}
                >
                  {"<"}
                </Button>
                <span>
                  Page{" "}
                  <strong>
                    {pageIndex + 1} of {getPageCount()}
                  </strong>
                </span>
                <Button onClick={() => nextPage()} disabled={!getCanNextPage()}>
                  {">"}
                </Button>
                <Button
                  onClick={() => setPageIndex(getPageCount() - 1)}
                  disabled={!getCanNextPage()}
                >
                  {">>"}
                </Button>
              </div>
            </Col>
          </Row>
        </CContainer>
      )}
    </>
  );
}

TNTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  paginationData: PropTypes.object,
  t: PropTypes.func,
  idName: PropTypes.string,
  onSelectPage: PropTypes.func.isRequired,
  pageIndexGet: PropTypes.number,
  isLoading: PropTypes.bool,
  responsive: PropTypes.bool,
  className: PropTypes.string,
};

export default TNTable;
